import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  DocumentTextIcon,
  LinkIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  PencilIcon,
  FolderOpenIcon,
  DocumentArrowUpIcon,
  CalculatorIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { CredentialsCheck } from './CredentialsCheck';

interface Recipient {
  email: string;
  businessName: string;
  contactName: string;
  customUrl?: string;
}

export const TaxPreparerFileRequest: React.FC = () => {
  const [businessName, setBusinessName] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [emailTemplate, setEmailTemplate] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Tax preparer templates
  const templates = {
    taxDocuments: `Hi {contactName},

It's that time of year! 📊 Please upload your tax documents using this secure link:

{uploadLink}

📁 Documents Needed:
• W-2 forms from all employers
• 1099 forms (if applicable)
• Previous year's tax return
• Business expense receipts
• Mortgage interest statements (1098)
• Investment income statements
• Charitable donation receipts

This link is secure and encrypted. Simply drag and drop your files or click to upload.

Questions? Reply to this email or call me directly.

Best regards,
Your Tax Preparer`,

    quickRequest: `Hi {contactName},

Please send me your tax documents! 📋

Upload them securely here:
{uploadLink}

This link is specifically for your files and completely secure.

Thanks!`,

    urgentRequest: `Hi {contactName},

⏰ Tax deadline approaching!

Please upload your documents ASAP:
{uploadLink}

Need these documents:
✓ All W-2s and 1099s
✓ Last year's return
✓ Any deduction receipts

The sooner you upload, the sooner we can file!

Thanks!`,

    businessTax: `Hi {contactName},

Time to prepare {businessName}'s tax filings.

Please upload all business documents here:
{uploadLink}

Required:
• Profit & Loss statements
• Balance sheets
• Bank statements
• Expense receipts
• Payroll records
• 1099s issued/received

Let's get your business taxes done right!

Best regards,
Your Tax Professional`
  };

  useEffect(() => {
    // Set default template
    setEmailTemplate(templates.taxDocuments);
  }, []);

  useEffect(() => {
    // Auto-generate URL slug from business name
    if (!editingUrl && businessName) {
      const slug = businessName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setUrlSlug(slug);
    }
  }, [businessName, editingUrl]);

  const handleSend = async () => {
    setSending(true);

    // Create business route in localStorage
    const routes = JSON.parse(localStorage.getItem('interactive_business_routes') || '[]');
    const existingRoute = routes.find((r: any) => r.slug === urlSlug);

    if (!existingRoute) {
      routes.push({
        id: Date.now(),
        slug: urlSlug,
        businessName: businessName,
        password: Math.random().toString(36).substr(2, 9),
        createdAt: new Date().toISOString(),
        files: [],
        taxPreparer: true
      });
      localStorage.setItem('interactive_business_routes', JSON.stringify(routes));
    }

    // Import and use the email service
    const { sendEmail } = await import('../services/emailService');

    try {
      // Send real email using AWS SES
      const result = await sendEmail({
        to: contactEmail,
        subject: `Tax Documents Needed - ${businessName}`,
        body: getEmailPreview(),
        businessName,
        uploadLink: getUploadUrl()
      });

      if (result.success) {
        // Log the successful send
        const log = JSON.parse(localStorage.getItem('tax_file_requests') || '[]');
        log.push({
          timestamp: new Date().toISOString(),
          recipient: contactEmail,
          businessName,
          urlSlug,
          uploadLink: getUploadUrl(),
          status: 'sent',
          messageId: result.messageId
        });
        localStorage.setItem('tax_file_requests', JSON.stringify(log));

        setSending(false);
        setSent(true);
      } else {
        alert(result.error || 'Failed to send email. Please check your email settings.');
        setSending(false);
      }
    } catch (error: any) {
      console.error('Email send error:', error);
      alert('Failed to send email: ' + error.message);
      setSending(false);
    }
  };

  const getUploadUrl = () => {
    return `${window.location.origin}/files/${urlSlug || 'your-custom-url'}`;
  };

  const getEmailPreview = () => {
    return emailTemplate
      .replace('{contactName}', contactName || '[Contact Name]')
      .replace('{businessName}', businessName || '[Business Name]')
      .replace('{uploadLink}', getUploadUrl());
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-green-600 p-8">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <CalculatorIcon className="w-8 h-8 text-blue-600" />
              Tax Document Request Portal
            </h1>
            <div className="flex items-center gap-4">
              <button
                onClick={async () => {
                  const { testSESConnection } = await import('../services/emailService');
                  const result = await testSESConnection();
                  alert(result ? '✅ SES is working! Test email sent to noreply@biig.dev' : '❌ SES connection failed. Check console for details.');
                }}
                className="text-sm px-3 py-1 bg-gray-100 rounded hover:bg-gray-200"
              >
                Test SES
              </button>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <ClipboardDocumentCheckIcon className="w-4 h-4" />
                Quick File Collection
              </div>
            </div>
          </div>

          {!sent ? (
            <div className="grid grid-cols-2 gap-8">
              {/* Left Column - Setup */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FolderOpenIcon className="w-5 h-5" />
                    Client Information
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Business/Client Name</label>
                      <input
                        type="text"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Smith Family Trust"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Upload URL Slug</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={urlSlug}
                          onChange={(e) => setUrlSlug(e.target.value)}
                          onFocus={() => setEditingUrl(true)}
                          onBlur={() => setEditingUrl(false)}
                          className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="smith-family-trust"
                        />
                        <button
                          onClick={() => setEditingUrl(!editingUrl)}
                          className="p-2 text-gray-500 hover:text-blue-600"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {getUploadUrl()}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Contact Name</label>
                      <input
                        type="text"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="John Smith"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Client Email</label>
                      <input
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="client@example.com"
                      />
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={() => setContactEmail('b@biig.dev')}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          Use b@biig.dev
                        </button>
                        <button
                          type="button"
                          onClick={() => setContactEmail('ideabrian@gmail.com')}
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          Use ideabrian@gmail.com
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        ✓ These emails are verified in AWS SES Sandbox
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold mb-3">Quick Templates</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setEmailTemplate(templates.taxDocuments)}
                      className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      📊 Full Tax Docs
                    </button>
                    <button
                      onClick={() => setEmailTemplate(templates.quickRequest)}
                      className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      ⚡ Quick Request
                    </button>
                    <button
                      onClick={() => setEmailTemplate(templates.urgentRequest)}
                      className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      ⏰ Urgent
                    </button>
                    <button
                      onClick={() => setEmailTemplate(templates.businessTax)}
                      className="px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      🏢 Business
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Email Message</label>
                  <textarea
                    value={emailTemplate}
                    onChange={(e) => setEmailTemplate(e.target.value)}
                    className="w-full px-4 py-2 border rounded-lg h-48 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <DocumentTextIcon className="w-5 h-5" />
                    Email Preview
                  </h2>

                  <div className="bg-gray-50 rounded-lg p-6 border">
                    <div className="mb-4 pb-4 border-b">
                      <div className="text-sm text-gray-600">To: {contactEmail || '[Client Email]'}</div>
                      <div className="text-sm text-gray-600">From: tax@yourfirm.com</div>
                      <div className="font-semibold mt-2">Subject: Tax Documents Needed - {businessName || '[Business Name]'}</div>
                    </div>

                    <div className="whitespace-pre-wrap text-sm">
                      {getEmailPreview()}
                    </div>
                  </div>
                </div>

                {/* Upload Link Preview */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <LinkIcon className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-900">Client's Upload Link</span>
                  </div>
                  <div className="bg-white rounded px-3 py-2 text-sm font-mono text-blue-600 break-all">
                    {getUploadUrl()}
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    ✓ Secure & Encrypted • ✓ Auto-organized • ✓ Instant notifications
                  </p>
                </div>

                {/* Send Button */}
                <button
                  onClick={handleSend}
                  disabled={!businessName || !contactEmail || !urlSlug || sending}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Sending Request...
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-5 h-5" />
                      Send File Request
                    </>
                  )}
                </button>

                <div className="text-xs text-gray-500 text-center">
                  Client will receive an email with a secure link to upload their tax documents directly to your portal
                </div>
              </div>
            </div>
          ) : (
            /* Success Screen */
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <CheckCircleIcon className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">File Request Sent!</h2>
              <p className="text-gray-600 mb-6">
                {contactName} will receive an email with their secure upload link
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6 max-w-2xl mx-auto">
                <p className="text-sm text-gray-600 mb-2">Upload Link:</p>
                <p className="font-mono text-blue-600 break-all">{getUploadUrl()}</p>
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    setSent(false);
                    setBusinessName('');
                    setUrlSlug('');
                    setContactEmail('');
                    setContactName('');
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Send Another Request
                </button>
                <a
                  href={`/files/${urlSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  View Upload Page
                </a>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};